import { Container, StateStream, Stream } from '@cgs/wge';
import { Lazy } from '@cgs/shared';
import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import { MachineStateDto } from '@cgs/provider-cgs';
import {
  BUY_FEATURE_POPUP_COMPONENT_TOKEN,
  BuyFeaturePopupComponent,
  CHEAT_COMPONENT_TOKEN,
  CheatComponent,
  CheatPopup,
  CheatPopupToken,
  FAST_SPINS_COMPONENT,
  FastSpinsComponent,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  GameStateMachineStates,
  HELP_POPUP_COMPONENT_TOKEN,
  HelpPopupComponent,
  IGameStateMachineProvider,
  PAYTABLE_POPUP_COMPONENT_TOKEN,
  PaytablePopupComponent,
} from '@cgs/machines';
import { ResumeDataDto, TrifectaSpinResultResponse } from '../../../models/trifecta-dto';
import { TrifectaAutoplayRules } from '../../../constants/trifecta-autoplay.constants';
import {
  TRIFECTA_SDK_ADAPTER_TOKEN,
  TrifectaSDKAdapter,
  TrifectaSDKErrorCode,
  TrifectaSDKGameHook,
  TrifectaSDKSpinType,
  TrifectaSDKTurboSpinMode,
} from '../../../sdk/trifecta-sdk.adapter';

export const TRIFECTA_GAME_HOOK_TOKEN = Symbol('TRIFECTA_GAME_HOOK_TOKEN');

// The reel-spinning window in which a StopEvent means anything: the only rule
// consuming it is stopping → immediatelyStop. The SDK's UiSpinButton does not
// check KeyboardEvent.repeat, so a held spacebar clicks the spin button at the
// OS key-repeat rate and the overlay routes every click of an active round
// into fastForward(). Dispatching those StopEvents outside this window spams
// the state machine across round boundaries (spinRequest/regularSpins) and
// races it into a broken stop sequence.
const REEL_SPINNING_STATE_IDS: string[] = [
  GameStateMachineStates.Accelerate,
  GameStateMachineStates.WaitRequest,
  GameStateMachineStates.WaitAccelerationComplete,
  GameStateMachineStates.Stopping,
];

export interface TrifectaGameHookParams {
  machineId: number;
  fastSpins: {
    firstSpeed: number;
    secondSpeed: number;
  };
  skipDoStopOnMaxSpeed?: boolean;
}

export abstract class TrifectaGameHook implements TrifectaSDKGameHook {
  public readonly gameResumed: Stream<void> = new Stream();
  public readonly gambleTriggered: Stream<number> = new Stream();

  protected readonly gameStateMachine: GameStateMachine<SpinResponse>;
  protected readonly trifectaSDKAdapter: TrifectaSDKAdapter;

  private readonly cheatPopup: Lazy<CheatPopup | null>;
  private readonly buyFeaturePopupComponent: BuyFeaturePopupComponent | null;
  private readonly gameRulesPopup: Lazy<PaytablePopupComponent>;
  private readonly helpPopup: Lazy<HelpPopupComponent>;
  private readonly fastSpinsComponent: FastSpinsComponent;
  private readonly cheatComponent: CheatComponent;

  constructor(
    protected readonly container: Container,
    protected readonly params: TrifectaGameHookParams
  ) {
    this.cheatPopup = new Lazy(() =>
      container.resolve<CheatPopup>(CheatPopupToken, { optional: true })
    );
    this.trifectaSDKAdapter = container.resolve<TrifectaSDKAdapter>(TRIFECTA_SDK_ADAPTER_TOKEN);
    this.buyFeaturePopupComponent = container.resolve<BuyFeaturePopupComponent>(
      BUY_FEATURE_POPUP_COMPONENT_TOKEN,
      {
        optional: true,
      }
    );
    this.gameRulesPopup = new Lazy(() =>
      container.resolve<PaytablePopupComponent>(PAYTABLE_POPUP_COMPONENT_TOKEN)
    );
    this.helpPopup = new Lazy(() =>
      container.resolve<HelpPopupComponent>(HELP_POPUP_COMPONENT_TOKEN)
    );
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    this.fastSpinsComponent = container.resolve<FastSpinsComponent>(FAST_SPINS_COMPONENT);
    this.cheatComponent = container.resolve<CheatComponent>(CHEAT_COMPONENT_TOKEN);
  }

  public shouldUseForceSpinAction(): boolean {
    return !!this.getSpinActionClientData();
  }

  // The SDK's IGameHook declares play/playGamble as void-returning, so the async
  // flow is delegated to handlePlay/handlePlayGamble and intentionally not awaited.
  public play(
    response: TrifectaSpinResultResponse | ResumeDataDto | Error,
    onSuccess: () => void,
    onError: (err: string) => void
  ): void {
    void this.handlePlay(response, onSuccess, onError);
  }

  protected abstract handlePlay(
    response: TrifectaSpinResultResponse | ResumeDataDto | Error,
    onSuccess: () => void,
    onError: (err: string) => void
  ): Promise<void>;

  public playGamble(response: any, onSuccess: () => void, onError: (err: string) => void): void {
    void this.handlePlayGamble(response, onSuccess, onError);
  }

  protected handlePlayGamble(
    _response: any,
    _onSuccess: () => void,
    _onError: (err: string) => void
  ): Promise<void> {
    return Promise.resolve();
  }

  protected checkAutoplayConditions(machineState: MachineStateDto): void {
    const stopOnAnyWin = this.trifectaSDKAdapter.getAutoPlayRuleValue(
      TrifectaAutoplayRules.StopOnAnyWin
    );

    if (
      stopOnAnyWin?.isActive &&
      machineState.totalWin &&
      machineState.totalWin > 0 &&
      (!machineState.freeSpinsInfo ||
        machineState.freeSpinsInfo?.event === FreeSpinsInfoEvent.FreeSpinsStarted)
    ) {
      this.trifectaSDKAdapter.abortAutoPlay();
    }

    const stopOnBonusWin = this.trifectaSDKAdapter.getAutoPlayRuleValue(
      TrifectaAutoplayRules.StopOnBonusWin
    );

    if (stopOnBonusWin?.isActive && (machineState.bonusInfo || machineState.freeSpinsInfo)) {
      this.trifectaSDKAdapter.abortAutoPlay();
    }
  }

  public onError(error: any): void {
    if (
      (error instanceof Error && error.message === TrifectaSDKErrorCode.InsufficientFunds) ||
      error === TrifectaSDKErrorCode.InsufficientFunds
    ) {
      this.onInsufficientFundsError();
    }
  }

  protected onInsufficientFundsError(): void {}

  public continue(): void {
    //
  }

  public pause(): void {
    //
  }

  // The SDK toggles fullscreen itself (DefaultGameOverlayBehaviour) and only
  // notifies the hook afterwards, so no reaction is needed on our side.
  public onFullscreenChanged(_isFullscreen: boolean): void {
    //
  }

  public fastForward(): void {
    const isMaxSpeed =
      (this.trifectaSDKAdapter.config.turboSpinMode as number) === TrifectaSDKTurboSpinMode.x4;

    if (this.params.skipDoStopOnMaxSpeed && isMaxSpeed) {
      return;
    }

    const isReelsSpinning = REEL_SPINNING_STATE_IDS.some((stateId) =>
      this.gameStateMachine.isActive(stateId)
    );

    if (!isReelsSpinning) {
      return;
    }

    this.gameStateMachine.doStop();
  }

  public getSpinActionClientData(): any {
    const cheat = this.cheatComponent.getSpinCheat();

    return cheat
      ? {
          cheat: this.cheatComponent.getSpinCheat()?.toJson(),
        }
      : null;
  }

  public onConfigChanged(): void {
    const turboSpinMode = this.trifectaSDKAdapter.config.turboSpinMode as number;

    if (turboSpinMode === 0) {
      this.fastSpinsComponent.toggleFastSpin(false);
    } else {
      const speedMapper = new Map<number, number>([
        [TrifectaSDKTurboSpinMode.x2, this.params.fastSpins.firstSpeed],
        [TrifectaSDKTurboSpinMode.x4, this.params.fastSpins.secondSpeed],
      ]);

      this.fastSpinsComponent.setSpeedMultiplier(speedMapper.get(turboSpinMode)!);
      this.fastSpinsComponent.toggleFastSpin(turboSpinMode > 0);
    }
  }

  public onBuyFeaturesButtonClicked(): void {
    if (this.buyFeaturePopupComponent) {
      this.trifectaSDKAdapter.hideControlPanel();
      this.buyFeaturePopupComponent.show();
      this.buyFeaturePopupComponent.isShown.once(() => this.trifectaSDKAdapter.showControlPanel());
    }
  }

  public onDebugButtonClicked(): void {
    this.cheatPopup.call()?.show();
  }

  public readonly autoPlayChanged: StateStream<boolean> = new StateStream<boolean>(false);

  public onAutoPlayStarted(): void {
    this.autoPlayChanged.emit(true);
  }

  public onAutoPlayStopped(): void {
    this.autoPlayChanged.emit(false);
  }

  public onSpinStarted(spinType: TrifectaSDKSpinType): void {
    if (spinType === 'buyFeature') {
      this.gameStateMachine.buyFreeSpins();
    } else {
      this.gameStateMachine.doSpin();
    }
  }

  public onSpinStopped(): void {
    //
  }

  public onHelpButtonClicked(): void {
    void this.showHelpPopup();
  }

  private async showHelpPopup(): Promise<void> {
    this.trifectaSDKAdapter.hideContainer();
    const helpPopup = this.helpPopup.call();
    helpPopup.show();
    await helpPopup.isShown.filter((isShown) => !isShown).toPromise();
    this.trifectaSDKAdapter.showContainer();
  }

  public onGameRulesButtonClicked(): void {
    void this.showGameRulesPopup();
  }

  private async showGameRulesPopup(): Promise<void> {
    this.trifectaSDKAdapter.hideContainer();
    const gameRulesPopup = this.gameRulesPopup.call();
    gameRulesPopup.show();
    await gameRulesPopup.isShown.filter((isShown) => !isShown).toPromise();
    this.trifectaSDKAdapter.showContainer();
  }

  public onGambleTriggered(gambleValue: number): void {
    this.gambleTriggered.emit(gambleValue);
  }

  protected get roundFinished(): Promise<unknown> {
    return Promise.any([
      this.gameStateMachine.idle.entered.first,
      this.gameStateMachine.regularSpins.entered.first,
    ]);
  }
}
